/**
 * User Manual Page
 * Visar alla menyer och funktioner i systemet med sökfunktion och hierarkisk struktur
 */

import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { MagnifyingGlassIcon, ArrowLeftIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { useUserManual } from '@/hooks/useUserManual';
import { iconCategories } from '@/constants/manualPages';

const UserManual = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    loading,
    searchQuery,
    setSearchQuery,
    expandedSections,
    filteredSections,
    availableIcons,
    toggleSection,
  } = useUserManual(t);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-textTertiary">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(ROUTES.HOME)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <BookOpenIcon className="w-6 h-6 text-primary" />
          {t('manual.title')}
        </h1>
      </div>

      {/* Sökfunktion */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textTertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('manual.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bgElevated text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
          />
        </div>
      </div>

      {/* Manual Sections */}
      <div className="space-y-4">
        {filteredSections.map(section => {
          const isExpanded = expandedSections.has(section.title);
          
          return (
            <div key={section.title} className="border border-border rounded-lg bg-bgContainer">
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-bgElevated transition-colors"
              >
                <h2 className="text-xl font-semibold text-textDefault">{section.title}</h2>
                <span className="text-textTertiary">
                  {isExpanded ? '▼' : '▶'}
                </span>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3">
                  {section.items.map(item => (
                    <div
                      key={item.featureCode}
                      className="border-l-2 border-bgPrimary pl-4 py-2"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-lg mt-1">{item.icon}</span>
                        <div className="flex-1">
                          <h3 className="font-semibold text-textDefault mb-1">
                            {item.label}
                          </h3>
                          {item.description && (
                            <p className="text-sm text-textTertiary mb-2">
                              {item.description}
                            </p>
                          )}
                          <p className="text-xs text-textSecondary mb-2">
                            <span className="font-medium">{t('manual.path')}:</span> {item.path}
                          </p>
                          {item.actions && item.actions.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-textSecondary mb-1">
                                {t('manual.availableActions')}:
                              </p>
                              <ul className="list-disc list-inside text-xs text-textTertiary space-y-1">
                                {item.actions.map(action => (
                                  <li key={action.id}>
                                    <span className="font-medium">{action.name}</span>
                                    {action.description && (
                                      <span className="text-textTertiary"> - {action.description}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredSections.length === 0 && (
        <div className="text-center py-8 text-textTertiary">
          {t('manual.noResults')}
        </div>
      )}

      {/* Guides / Processes Section */}
      <div className="mt-8 border border-border rounded-lg bg-bgContainer">
        <button
          onClick={() => toggleSection(t('manual.guides.title'))}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-bgElevated transition-colors"
        >
          <h2 className="text-xl font-semibold text-textDefault">{t('manual.guides.title')}</h2>
          <span className="text-textTertiary">
            {expandedSections.has(t('manual.guides.title')) ? '▼' : '▶'}
          </span>
        </button>

        {expandedSections.has(t('manual.guides.title')) && (
          <div className="px-4 pb-4 space-y-4">
            <div className="border-l-2 border-bgPrimary pl-4 py-2">
              <h3 className="font-semibold text-textDefault mb-1">{t('manual.guides.deviceVehicleTitle')}</h3>
              <p className="text-sm text-textTertiary mb-3">{t('manual.guides.deviceVehicleIntro')}</p>

              {(['auto', 'manual'] as const).map(block => {
                const steps = t(`manual.guides.${block}Steps`, { returnObjects: true }) as unknown;
                const stepList = Array.isArray(steps) ? (steps as string[]) : [];
                return (
                  <div key={block} className="mb-3">
                    <p className="text-sm font-medium text-textSecondary mb-1">
                      {t(`manual.guides.${block}Heading`)}
                    </p>
                    <ul className="list-disc list-inside text-xs text-textTertiary space-y-1">
                      {stepList.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Icons Section */}
      <div className="mt-8 border border-border rounded-lg bg-bgContainer">
        <button
          onClick={() => toggleSection(t('manual.icons'))}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-bgElevated transition-colors"
        >
          <h2 className="text-xl font-semibold text-textDefault">{t('manual.icons')}</h2>
          <span className="text-textTertiary">
            {expandedSections.has(t('manual.icons')) ? '▼' : '▶'}
          </span>
        </button>

        {expandedSections.has(t('manual.icons')) && (
          <div className="px-4 pb-4">
            <p className="text-sm text-textTertiary mb-4">
              {t('manual.iconsDescription')}
            </p>
            
            {/* Group icons by category */}
            {Object.keys(iconCategories).map(category => {
              const categoryIcons = availableIcons.filter(icon => icon.category === category);
              if (categoryIcons.length === 0) return null;

              return (
                <div key={category} className="mb-6">
                  <h3 className="text-lg font-semibold text-textDefault mb-3">
                    {t(`manual.${category}Icons`) || category.charAt(0).toUpperCase() + category.slice(1)}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {categoryIcons.map((iconInfo, idx) => (
                      <div
                        key={idx}
                        className="p-4 border border-border rounded-lg bg-bgElevated flex flex-col items-center gap-2 hover:border-bgPrimary transition-colors"
                      >
                        <div className="text-2xl text-textDefault">
                          {iconInfo.icon}
                        </div>
                        <div className="text-center w-full">
                          <div className="text-xs font-medium text-textDefault break-words">
                            {iconInfo.name}
                          </div>
                          {iconInfo.description !== iconInfo.name && (
                            <div className="text-xs text-textTertiary mt-1">
                              {iconInfo.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManual;
